import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { fetchClientUninvoicedEntries } from '@/api/entries';
import { fetchCreditsAndFees } from '@/api/misc';
import { createPaymentLink, creditFeeAmount } from '@/api/invoices';
import { useCanTakePayments, useCreateInvoice, useSendInvoice } from '@/hooks/useInvoices';
import { useClients, useNameLookup } from '@/hooks/useReference';
import { useCompanyInfo, useInvoiceSettings } from '@/hooks/useSettings';
import { useBillingNouns, useSession } from '@/hooks/useSession';
import { qk } from '@/lib/query';
import { addDays, money, moneyCompact, today, toDayString } from '@/lib/format';
import type { Client, TimeEntry } from '@/types';
import { Sheet, SheetBack } from '@m/ui/Sheet';
import { PickerList } from '@m/ui/Picker';
import { Card, ColorDot, Group, Row, SectionHeader } from '@m/ui/List';
import { BarButton, Button, SkeletonList, Switch } from '@m/ui/Controls';
import { cn } from '@m/ui/cn';
import { shortDay } from './Invoices';

type Step = 'client' | 'entries' | 'review';
type Period = 'all' | 'this' | 'last';

const PERIOD_ORDER: Period[] = ['all', 'this', 'last'];

function periodRange(p: Period): [string, string] | null {
  if (p === 'all') return null;
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - (p === 'last' ? 1 : 0), 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return [toDayString(first), toDayString(last)];
}

function periodLabel(p: Period, entries: TimeEntry[]) {
  const range = periodRange(p);
  if (range) return `${shortDay(range[0])} – ${shortDay(range[1])}`;
  if (!entries.length) return 'All unbilled';
  const days = entries.map((e) => e.date).sort();
  return `${shortDay(days[0])} – ${shortDay(days[days.length - 1])}`;
}

const h1 = (v: number) => (Math.round(v * 10) / 10).toFixed(1);

/**
 * New invoice (S09/S10): client, then the unbilled time to put on it, then a
 * review with Send or Save as draft. Opened from the Invoices tab and from a
 * client's "Invoice time" (`clientId` skips the first step).
 */
export function NewInvoiceSheet({ open, onClose, clientId }: { open: boolean; onClose: () => void; clientId?: string }) {
  if (!open) return null;
  return <Flow onClose={onClose} initialClientId={clientId} />;
}

function Flow({ onClose, initialClientId }: { onClose: () => void; initialClientId?: string }) {
  const navigate = useNavigate();
  const session = useSession();
  const nouns = useBillingNouns();
  const clients = useClients().data ?? [];
  const settings = useInvoiceSettings().data;
  const company = useCompanyInfo().data;
  const canTakePayments = useCanTakePayments();
  const { activityName } = useNameLookup();
  const create = useCreateInvoice();
  const send = useSendInvoice();

  const [step, setStep] = useState<Step>(initialClientId ? 'entries' : 'client');
  const [clientId, setClientId] = useState<string | null>(initialClientId ?? null);
  const [period, setPeriod] = useState<Period>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cardPayment, setCardPayment] = useState(true);
  const [busy, setBusy] = useState(false);

  const client: Client | null = clients.find((c) => c.id === clientId) ?? null;

  // Never from cache: a stale list here is an entry billed twice.
  const entries = useQuery({
    queryKey: qk.uninvoicedEntries(clientId ?? ''),
    queryFn: ({ signal }) => fetchClientUninvoicedEntries(clientId!, signal),
    enabled: Boolean(clientId),
    staleTime: 0,
    refetchOnMount: 'always'
  });
  const openFees = useQuery({
    queryKey: [...qk.creditsAndFeesRoot, 'open', clientId ?? ''] as const,
    queryFn: ({ signal }) => fetchCreditsAndFees({ clientId: clientId!, invoiced: false, sortOrder: 'asc' }, signal),
    enabled: Boolean(clientId),
    staleTime: 0
  });

  const inPeriod = useMemo(() => {
    const range = periodRange(period);
    const list = [...(entries.data ?? [])].sort((a, b) => (a.date < b.date ? 1 : -1));
    return range ? list.filter((e) => e.date >= range[0] && e.date <= range[1]) : list;
  }, [entries.data, period]);

  // Everything in the period starts selected; a new client or period resets it.
  useEffect(() => {
    setSelected(new Set(inPeriod.map((e) => e.id)));
  }, [inPeriod]);

  const rate = client?.hourlyRate ?? settings?.defaultHourlyRate ?? 0;
  const chosen = inPeriod.filter((e) => selected.has(e.id));
  const totalHours = chosen.reduce((s, e) => s + e.time, 0);
  const fees = openFees.data?.items ?? [];
  const feesTotal = fees.reduce((s, f) => s + creditFeeAmount(f), 0);
  const totalAmount = totalHours * rate + feesTotal;
  const missingPayer = Boolean(client) && !client?.billable;
  const terms = settings?.paymentTermsDays ?? 30;
  const dueDate = addDays(today(), terms);
  const canCreate = Boolean(client) && !missingPayer && rate > 0 && (chosen.length > 0 || fees.length > 0);
  const dirty = step !== (initialClientId ? 'entries' : 'client');

  const finish = async (sendIt: boolean) => {
    if (!client || !canCreate) return;
    setBusy(true);
    try {
      const invoice = await create.mutateAsync({
        clientId: client.id,
        billableId: client.billable?.id ?? null,
        hourlyRate: rate,
        entryIds: chosen.map((e) => e.id),
        totalHours,
        totalAmount,
        issueDate: today(),
        dueDate,
        status: 'draft',
        creditsAndFees: fees,
        silent: sendIt
      });
      if (canTakePayments && cardPayment) {
        try {
          await createPaymentLink(invoice.id);
        } catch {
          toast.error('The invoice was created, but the card payment link was not.');
        }
      }
      if (sendIt) {
        try {
          await send.mutateAsync(invoice.id);
        } catch {
          // useSendInvoice toasts its own error; the draft is still there to send.
        }
      }
      onClose();
      navigate(`/invoices/${invoice.id}`);
    } catch {
      // useCreateInvoice toasted; stay on the review.
    } finally {
      setBusy(false);
    }
  };

  const allSelected = inPeriod.length > 0 && chosen.length === inPeriod.length;

  const header =
    step === 'client'
      ? { leading: <BarButton onClick={onClose}>Cancel</BarButton>, trailing: null, subtitle: 'Step 1 of 3' }
      : step === 'entries'
        ? {
            leading: <BarButton onClick={onClose}>Cancel</BarButton>,
            trailing: (
              <BarButton className="font-semibold" disabled={!canCreate} onClick={() => setStep('review')}>
                Next
              </BarButton>
            ),
            subtitle: 'Step 2 of 3'
          }
        : { leading: <SheetBack label="Entries" onClick={() => setStep('entries')} />, trailing: null, subtitle: 'Step 3 of 3' };

  const footer =
    step === 'entries' ? (
      <div className="flex items-center gap-3 border-t bg-[var(--surface)]/95 px-4 pt-3 pb-3 backdrop-blur-xl">
        <span className="flex flex-1 flex-col">
          <span className="tnum text-[20px] leading-7 font-semibold">{money(totalAmount)}</span>
          <span className="text-caption text-[var(--text-muted)]">
            {chosen.length} {chosen.length === 1 ? 'entry' : 'entries'} · {h1(totalHours)} h
          </span>
        </span>
        <Button block={false} className="px-7" disabled={!canCreate} onClick={() => setStep('review')}>
          Review
        </Button>
      </div>
    ) : step === 'review' ? (
      <div className="flex flex-col gap-1.5 px-4 pt-2">
        {session.isAdmin && (
          <Button disabled={!canCreate || busy} onClick={() => void finish(true)}>
            {busy ? 'Working…' : 'Send invoice'}
          </Button>
        )}
        <Button variant={session.isAdmin ? 'plain' : 'primary'} disabled={!canCreate || busy} onClick={() => void finish(false)}>
          Save as draft
        </Button>
      </div>
    ) : undefined;

  return (
    <Sheet open onClose={onClose} full dirty={dirty} title={step === 'review' ? 'Review' : 'New invoice'} subtitle={header.subtitle} leading={header.leading} trailing={header.trailing} footer={footer}>
      {step === 'client' && (
        <PickerList
          items={clients.filter((c) => c.status === 'active')}
          value={clientId}
          noun={nouns.clientLower}
          nounPlural={nouns.clients.toLowerCase()}
          leading={(c) => <ColorDot id={c.id} />}
          onSelect={(c) => {
            setClientId(c.id);
            setPeriod('all');
            setStep('entries');
          }}
        />
      )}

      {step === 'entries' && client && (
        <div className="flex flex-col gap-[18px]">
          <Group>
            <Row
              title={nouns.client}
              detail={
                <span className="inline-flex items-center gap-2">
                  <ColorDot id={client.id} />
                  {client.name}
                </span>
              }
              onClick={() => setStep('client')}
              chevron
            />
            <button
              type="button"
              onClick={() => setPeriod(PERIOD_ORDER[(PERIOD_ORDER.indexOf(period) + 1) % PERIOD_ORDER.length]!)}
              className="flex min-h-[52px] w-full items-center gap-3 px-4 text-left"
            >
              <span className="flex-1">Period</span>
              <span className="flex h-[34px] items-center rounded-lg bg-[var(--page)] px-3">{periodLabel(period, entries.data ?? [])}</span>
            </button>
          </Group>

          {missingPayer && (
            <p className="px-4 text-caption text-[var(--danger)]">
              {client.name} has no {nouns.payer.toLowerCase()} to bill. Add one on the {nouns.clientLower}’s page first.
            </p>
          )}
          {rate <= 0 && !missingPayer && (
            <p className="px-4 text-caption text-[var(--danger)]">Set an hourly rate for {client.name} or a team default first.</p>
          )}

          <div className="flex flex-col gap-2">
            <SectionHeader
              right={
                inPeriod.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelected(allSelected ? new Set() : new Set(inPeriod.map((e) => e.id)))}
                    className="h-8 text-secondary font-medium normal-case text-[var(--accent)]"
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                )
              }
            >
              Unbilled time
            </SectionHeader>
            {entries.isPending ? (
              <SkeletonList rows={4} />
            ) : inPeriod.length === 0 ? (
              <div className="rounded-2xl bg-[var(--surface)] px-6 py-8 text-center text-secondary text-[var(--text-muted)]">
                No unbilled time {period === 'all' ? `for ${client.name}` : 'in this period'}.
              </div>
            ) : (
              <Group>
                {inPeriod.map((e) => {
                  const on = selected.has(e.id);
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => {
                        const next = new Set(selected);
                        if (on) next.delete(e.id);
                        else next.add(e.id);
                        setSelected(next);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
                    >
                      {on ? (
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-white">
                          <Check className="size-3.5" strokeWidth={3} />
                        </span>
                      ) : (
                        <span className="size-6 shrink-0 rounded-full border-2 border-zinc-400" />
                      )}
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className={cn('truncate text-body', !on && 'text-[var(--text-muted)]')}>{activityName(e.activityId)}</span>
                        <span className="truncate text-secondary text-[var(--text-muted)]">
                          {[shortDay(e.date), e.userName, e.misc].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <span className={cn('tnum text-body', !on && 'text-[var(--text-subtle)]')}>{h1(e.time)}</span>
                    </button>
                  );
                })}
              </Group>
            )}
          </div>
        </div>
      )}

      {step === 'review' && client && (
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col gap-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
            <span className="flex flex-col gap-0.5">
              <span className="text-secondary font-semibold">{company?.name || session.team?.name}</span>
              <span className="text-caption text-[var(--text-muted)]">
                New invoice
              </span>
            </span>
            <span className="h-px bg-[var(--line)]" />
            <span className="flex justify-between text-secondary">
              <span className="text-[var(--text-muted)]">{client.name}</span>
              <span className="tnum">{h1(totalHours)} h</span>
            </span>
            <span className="flex justify-between text-secondary">
              <span className="text-[var(--text-muted)]">{periodLabel(period, chosen)}</span>
              <span className="tnum">{moneyCompact(rate)} / h</span>
            </span>
            <span className="flex items-baseline justify-between">
              <span className="text-secondary font-semibold">Total due</span>
              <span className="tnum text-[28px] leading-9 font-semibold tracking-[-0.02em]">{money(totalAmount)}</span>
            </span>
          </Card>

          <Group>
            <Row title="To" detail={client.billable?.emails?.[0] ?? client.billable?.name ?? 'No email'} />
            <Row title="Due" detail={`Net ${terms} · ${shortDay(dueDate)}`} />
            <Row
              title="Credits & fees"
              detail={fees.length ? `${fees.length} · ${feesTotal < 0 ? '−' : ''}${money(Math.abs(feesTotal))}` : 'None'}
            />
            {canTakePayments && (
              <div className="flex min-h-[52px] items-center gap-3 px-4">
                <span className="flex-1">Accept card payment</span>
                <Switch checked={cardPayment} onChange={setCardPayment} label="Accept card payment" />
              </div>
            )}
          </Group>
          {!session.isAdmin && <p className="px-4 text-caption text-[var(--text-muted)]">An admin on your team sends invoices. Save it as a draft for them.</p>}
        </div>
      )}
    </Sheet>
  );
}

